import { z } from 'zod';

/**
 * Tabellen-Sicherung: Format für „Tabelle exportieren" (Datenbank-Seite) und das
 * Wiedereinspielen im Datenimport. Bewusst ein EIGENES Format neben dem
 * Altdaten-Import (ADR 0026):
 *
 * Der Altdaten-Import übersetzt FREMDE Daten (Feld-Mapping, EUR → Mikro,
 * Fremdschlüssel über Kundennummern) und kennt dafür genau neun Entitäten. Eine
 * Tabellen-Sicherung kommt dagegen aus demselben Schema — sie braucht keine
 * Übersetzung, dafür aber jede Tabelle der Allowlist, unter anderem `projects`
 * und `time_entries`, die der Altdaten-Import gar nicht kennt.
 *
 * Die Datei ist NDJSON und selbstbeschreibend: Zeile 1 ist der Kopf (dieses
 * Schema), danach folgt je Zeile ein Datensatz. Deshalb braucht sie — anders als
 * der Altdaten-Import — kein separates `manifest.json`; der Import erkennt sie
 * an `__proerp` in der ersten Zeile.
 */
export const TABLE_EXPORT_MARKER = 'proerp-table-export';

/** Formatversion. Erhöhen, sobald sich der Aufbau der Datei ändert. */
export const TABLE_EXPORT_VERSION = 1;

/** Kopfzeile (Zeile 1) einer Tabellen-Sicherung. */
export const tableExportHeaderSchema = z.object({
  __proerp: z.literal(TABLE_EXPORT_MARKER),
  version: z.number().int().positive(),
  /** DB-Tabellenname, z. B. `projects`. Muss in der Allowlist stehen. */
  table: z.string().min(1),
  /** Anzeigename zum Zeitpunkt des Exports, z. B. „Projekte". Nur für die Anzeige. */
  label: z.string().optional(),
  /** ISO-Zeitpunkt des Exports. */
  exportedAt: z.string(),
  /** Anzahl Datenzeilen nach dem Kopf — für Fortschritt und Plausibilitätsprüfung. */
  rowCount: z.number().int().nonnegative(),
  /** Spalten in Schreibreihenfolge (Anzeige + Vorschau). */
  columns: z.array(z.string()),
});
export type TableExportHeader = z.infer<typeof tableExportHeaderSchema>;

/**
 * Verhalten bei bereits vorhandener Zeile (gleiche `id`):
 * - `skip`      — vorhandene bleiben unangetastet, nur fehlende werden ergänzt.
 * - `overwrite` — der Stand aus der Datei gewinnt (Zurücksetzen auf den Export).
 */
export const tableRestoreModeSchema = z.enum(['skip', 'overwrite']);
export type TableRestoreMode = z.infer<typeof tableRestoreModeSchema>;

/** Anfrage der Konflikt-Vorschau: Wie viele dieser IDs gibt es schon? */
export const tableExistingCountSchema = z.object({
  ids: z.array(z.string().uuid()).max(50_000),
});
export type TableExistingCountInput = z.infer<typeof tableExistingCountSchema>;

/** Antwort der Konflikt-Vorschau. */
export const tableExistingCountResultSchema = z.object({
  /** Wie viele der übergebenen IDs bereits in der Tabelle liegen (mandantengescoped). */
  existing: z.number().int().nonnegative(),
  /** Wie viele IDs geprüft wurden — kann unter der Dateigröße liegen (Obergrenze). */
  checked: z.number().int().nonnegative(),
});
export type TableExistingCountResult = z.infer<typeof tableExistingCountResultSchema>;

/** Bilanz je wiederhergestellter Tabelle. */
export const tableRestoreTableResultSchema = z.object({
  table: z.string(),
  label: z.string(),
  inserted: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  /** Vorhanden gelassen (Modus `skip`). */
  skipped: z.number().int().nonnegative(),
  /**
   * Nicht einspielbar — fast immer eine fehlende Verknüpfung (die Zeile zeigt auf
   * einen Datensatz, den es hier nicht gibt). Bricht den Lauf NICHT ab.
   */
  failed: z.number().int().nonnegative(),
  /** Grund der ersten fehlgeschlagenen Zeile, damit man weiß, wonach man sucht. */
  firstError: z.string().nullable(),
});
export type TableRestoreTableResult = z.infer<typeof tableRestoreTableResultSchema>;

/** Gesamtergebnis eines Wiedereinspielens (kann mehrere Dateien umfassen). */
export const tableRestoreResultSchema = z.object({
  mode: tableRestoreModeSchema,
  tables: z.array(tableRestoreTableResultSchema),
});
export type TableRestoreResult = z.infer<typeof tableRestoreResultSchema>;

/**
 * Erkennt eine Tabellen-Sicherung an ihrer ersten Zeile. Gibt den Kopf zurück
 * oder `null`, wenn es keine ist (dann greift der normale Altdaten-Import).
 * Absichtlich tolerant: kein Wurf, damit ein Fremdformat einfach durchfällt.
 */
export function readTableExportHeader(firstLine: string): TableExportHeader | null {
  const trimmed = firstLine.replace(/^﻿/, '').trim();
  if (!trimmed.startsWith('{') || !trimmed.includes(TABLE_EXPORT_MARKER)) return null;
  try {
    const parsed = tableExportHeaderSchema.safeParse(JSON.parse(trimmed));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Dateiname einer Sicherung, z. B. `projects-2026-08-24.ndjson`. */
export function tableExportFileName(table: string, exportedAt: Date): string {
  const iso = exportedAt.toISOString().slice(0, 10);
  return `${table}-${iso}.ndjson`;
}
